import { Dialog } from "@headlessui/react";
import { IconRosetteDiscountCheckFilled, IconX } from "@tabler/icons-react";
import { Controller, useForm } from "react-hook-form";
import Button from "./Button";
import { connect, useDispatch } from "react-redux";
import { submitContractTicket } from "../store/contract/actions";
import toast from "react-hot-toast";
import { RootState } from "../store/root-reducer";
import { isScopeLoading } from "../store/scope-loading/helpers";
import { ContractScope } from "../store/contract/types";

interface ContractSubmitTicketModalProps {
  isOpen: boolean;
  onClose: () => void;
  loading: boolean;
}

function ContractSubmitTicketModal({
  isOpen,
  onClose,
  loading,
}: ContractSubmitTicketModalProps) {
  const {
    control,
    handleSubmit,
    formState: { isValid },
    reset,
  } = useForm({
    mode: "onChange",
    defaultValues: {
      reason: "",
      comment: "",
    },
  });

  const dispatch = useDispatch();

  const onSubmit = (data: any) => {
    dispatch(
      submitContractTicket(
        {
          data,
        },
        () => {
          onClose();
          toast.success("Your ticket has been submitted");
          reset({
            reason: "",
            comment: "",
          });
        }
      )
    );
  };

  return (
    <Dialog open={isOpen} onClose={onClose} className="fixed inset-0 z-50">
      <div className="fixed inset-0 bg-black/50" aria-hidden="true" />

      <div className="fixed inset-0 flex items-center justify-center p-4 sm:p-6">
        <div
          role="dialog"
          aria-modal="true"
          aria-labelledby="submit-ticket-title"
          className="w-full max-w-lg max-h-full overflow-hidden rounded-xl bg-white shadow-xl flex flex-col"
        >
          {/* Header */}
          <div className="flex items-center justify-between border-b border-gray-200 gap-3 px-4 py-3 text-gray-800">
            <h2
              id="submit-ticket-title"
              className="text-base sm:text-lg font-medium flex items-center gap-2"
            >
              <IconRosetteDiscountCheckFilled
                size={20}
                className="text-slate-500"
              />
              Submit Ticket
            </h2>
            <button
              onClick={onClose}
              className="text-gray-500 hover:text-gray-700"
            >
              <IconX />
            </button>
          </div>

          {/* Form Body */}
          <form
            onSubmit={handleSubmit(onSubmit)}
            className="flex flex-col gap-4 p-4 sm:p-6 flex-1 overflow-y-auto text-sm text-gray-700"
          >
            {/* Reason Dropdown */}
            <div>
              <label className="block mb-1 font-medium">Reason</label>
              <Controller
                control={control}
                name="reason"
                rules={{ required: "Please select a reason" }}
                render={({ field }) => (
                  <select
                    {...field}
                    className="w-full p-2 border border-gray-300 rounded-md"
                  >
                    <option value="">Select reason</option>
                    <option value="general">General</option>
                    <option value="urgent">Urgent</option>
                    <option value="escalation">Escalation</option>
                  </select>
                )}
              />
            </div>

            {/* Comment Box */}
            <div>
              <label className="block mb-1 font-medium">Comment</label>
              <Controller
                control={control}
                name="comment"
                rules={{
                  validate: (value = "") =>
                    value.trim().length > 0 || "Comment is required",
                }}
                render={({ field }) => (
                  <textarea
                    {...field}
                    rows={6}
                    className="w-full p-2 border border-gray-300 rounded-md resize-none"
                    placeholder="Write your comment..."
                  />
                )}
              />
            </div>

            {/* Footer */}
            <div className="flex justify-end gap-2 border-t border-gray-200 pt-4 mt-auto">
              <Button variant="blue" type="button" onClick={onClose}>
                Cancel
              </Button>
              <Button
                variant="blue"
                type="submit"
                disabled={!isValid || loading}
              >
                {loading ? "Submitting..." : "Submit"}
              </Button>
            </div>
          </form>
        </div>
      </div>
    </Dialog>
  );
}

const mapStateProps = (state: RootState) => {
  return {
    loading: isScopeLoading(state.scopeLoading, `contact-${ContractScope}`),
  };
};

export default connect(mapStateProps)(ContractSubmitTicketModal);
